import { useUserRewardsMe } from '@/api/user-rewards';
import { UserRewardsQueryKeys } from '@/api/user-rewards/queryKeys';
import { useUserAccount } from '@/api/user/queries';
import { ModalsKey } from '@/enums/modalsKey';
import { RewardId } from '@/enums/reward-id';
import { RootState } from '@/lib/store';
import { UserRewardStatusId } from '@/models/user-reward/status/id';
import { UserRewardTracker } from '@/models/user-reward/tracker';
import { useCountdown } from '@starsoft/common/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useModalNavigation } from './modals/useModalNavigation';

export function useRewardState(reward?: UserRewardTracker) {
  const { user } = useUserAccount();
  const queryClient = useQueryClient();

  const isAdminView: boolean = useSelector(
    (state: RootState) => state.theme.isAdminView,
  );
  const isLuckyWheel =
    reward?.rewardId?.toString() === RewardId.LuckyWheel.toString() ||
    reward?.rewardId?.toString() === RewardId.RollCompetition.toString();
  const isDisabled = isAdminView
    ? false
    : (reward?.counter <= 0 && reward?.lockedCounter <= 0 && !isLuckyWheel) ||
      !user;
  const { getModalLink } = useModalNavigation();
  const isCountdownVisible = reward?.counter + reward?.lockedCounter > 0;

  const { reward: userReward } = useUserRewardsMe({
    rewardId: reward?.rewardId,
  });

  const getLink = useCallback(() => {
    if (!user) {
      return getModalLink(ModalsKey.Login);
    }

    switch (reward?.rewardId?.toString()) {
      case RewardId.LuckyWheel.toString():
        return getModalLink(
          ModalsKey.LuckySpin,
          `?reward-id=${reward?.rewardId}&mode=1`,
        );
      case RewardId.RollCompetition.toString():
        return getModalLink(
          ModalsKey.RollCompetition,
          `?reward-id=${reward?.rewardId}`,
        );
      default:
        return getModalLink(
          ModalsKey.ClaimReward,
          `?reward-id=${reward?.rewardId}`,
        );
    }
  }, [getModalLink, user, reward]);

  const { hours, minutes, seconds, days } = useCountdown({
    targetDate:
      userReward?.reward?.statusId?.toString() ===
      UserRewardStatusId.InProgress.toString()
        ? new Date(userReward?.vesting?.expiresAt ?? new Date())?.toUTCString()
        : new Date(
            userReward?.vesting?.availableAt ?? new Date(),
          )?.toUTCString(),
    disabled: isDisabled,
    onCountdownComplete: ({ days, hours, minutes, seconds }) => {
      if (
        isDisabled ||
        (days === '00' &&
          hours === '00' &&
          minutes === '00' &&
          seconds === '00')
      ) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [UserRewardsQueryKeys.ById],
      });
      queryClient.invalidateQueries({
        queryKey: [UserRewardsQueryKeys.Me],
      });
      queryClient.invalidateQueries({
        queryKey: [UserRewardsQueryKeys.Trackers],
      });
    },
  });

  return {
    isDisabled,
    timer: {
      days,
      hours,
      minutes,
      seconds,
    },
    getLink,
    isCountdownVisible,
  };
}
